import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Image from '../../../components/AppImage';
import CommunicationService from '../../../services/communications';

const SocialMediaPanel = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('linkedin');
  const [messageContent, setMessageContent] = useState('');
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [postContent, setPostContent] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states  
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [socialMessages, setSocialMessages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [accountsRes, contactsRes, messagesRes, campaignsRes] = await Promise.all([
        CommunicationService?.getSocialAccounts?.() || Promise.resolve({ success: false }), 
        CommunicationService?.getContacts(),
        CommunicationService?.getMessages({ type: 'social_message', limit: 10 }),
        CommunicationService?.getCampaigns()
      ]);

      if (accountsRes?.success) {
        setSocialAccounts(accountsRes?.data || []);
      }
      if (contactsRes?.success) {
        setContacts(contactsRes?.data || []);
      }
      if (messagesRes?.success) {
        setSocialMessages(messagesRes?.data || []);
      }
      if (campaignsRes?.success) {
        setCampaigns(campaignsRes?.data?.filter(c => c?.type === 'social') || []);
      }
    } catch (error) {
      console.error('Error loading social media data:', error);
      setError('Failed to load social media data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageContent || selectedProspects?.length === 0) {
      setError('Please enter a message and select at least one prospect');
      return;
    }

    const selectedAccount = socialAccounts?.find(acc => acc?.platform === selectedPlatform && acc?.status === 'connected');
    if (!selectedAccount) {
      setError(`No connected ${selectedPlatform} account found. Please connect your account first.`);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let successCount = 0;
      const errors = [];

      for (const prospectId of selectedProspects) {
        const contact = contacts?.find(c => c?.id === prospectId);
        if (!contact?.email && !contact?.phone) {
          errors?.push(`${contact?.name || 'Unknown'}: No contact information`);
          continue;
        }

        // Create social message record
        const messageRes = await CommunicationService?.createMessage({
          type: 'social_message',
          direction: 'outbound',
          contact_id: prospectId,
          content: messageContent,
          to_address: `@${contact?.name?.toLowerCase()?.replace(' ', '')}`, // Simplified username
          from_address: `@${selectedAccount?.username}`,
          status: 'sent', // Assume sent for demo
          metadata: {
            platform: selectedPlatform,
            account_id: selectedAccount?.id,
            message_type: 'direct_message'
          }
        });

        if (messageRes?.success) {
          successCount++;
          // Track usage
          await CommunicationService?.trackUsage('social_messages', 1);
        } else {
          errors?.push(`${contact?.name || 'Unknown'}: ${messageRes?.error}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully sent ${successCount} social message${successCount > 1 ? 's' : ''} via ${selectedPlatform}`);
        setMessageContent('');
        setSelectedProspects([]);
        loadData(); // Refresh data
      }

      if (errors?.length > 0) {
        setError(`Errors: ${errors?.join(', ')}`);
      }
    } catch (error) {
      console.error('Error sending social messages:', error);
      setError('Failed to send social messages');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAccount = async (platform) => {
    // Simulate account connection - in real implementation, this would use OAuth flows
    setSuccess(`Redirecting to ${platform} for account connection...`);
    // This would typically open OAuth flow in a popup or redirect
  };

  const platforms = [
    { value: 'linkedin', label: 'LinkedIn', icon: 'Linkedin', color: 'text-blue-600' },
    { value: 'facebook', label: 'Facebook', icon: 'Facebook', color: 'text-blue-500' },
    { value: 'twitter', label: 'Twitter', icon: 'Twitter', color: 'text-sky-500' },
    { value: 'instagram', label: 'Instagram', icon: 'Instagram', color: 'text-pink-500' }
  ];

  // Mock prospects for demonstration - in real app would come from contacts with social profiles
  const prospects = contacts?.slice(0, 5)?.map((contact, index) => ({
    ...contact,
    platform: selectedPlatform,
    profileUrl: `https://${selectedPlatform}.com/in/${contact?.name?.toLowerCase()?.replace(' ', '-')}`,
    avatar: `https://images.unsplash.com/photo-${index % 2 === 0 ? '1494790108755-2616b612b786' : '1507003211169-0a1dd7228f2d'}?w=150`,
    connectionStatus: ['connected', 'pending', 'not_connected']?.[index % 3],
    lastInteraction: index === 0 ? '2 days ago' : index === 1 ? '1 week ago' : 'Never',
    engagement: ['high', 'medium', 'low']?.[index % 3]
  })) || [];

  const getPlatformIcon = (platform) => {
    const platformData = platforms?.find(p => p?.value === platform);
    return platformData ? platformData?.icon : 'Share2';
  };

  const getPlatformColor = (platform) => {
    const platformData = platforms?.find(p => p?.value === platform);
    return platformData ? platformData?.color : 'text-muted-foreground';
  };

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'not_connected':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getAccountStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-success/10 text-success border-success/20';
      case 'expired':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'disconnected':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getEngagementColor = (engagement) => {
    switch (engagement) {
      case 'high':
        return 'text-success';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Loading social media data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Connected Accounts Overview */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Connected Social Accounts</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {platforms?.map((platform) => {
            const account = socialAccounts?.find(acc => acc?.platform === platform?.value);
            const isConnected = account?.status === 'connected';
            
            return (
              <div
                key={platform?.value}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  isConnected ? getAccountStatusColor(account?.status) : 'border-border hover:bg-muted'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <Icon 
                    name={platform?.icon} 
                    size={24} 
                    className={isConnected ? 'text-success' : platform?.color}
                  />
                  <span className="text-sm font-medium">{platform?.label}</span>
                  {isConnected ? (
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">@{account?.username}</p>
                      <p className="text-xs text-success font-medium">Connected</p>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConnectAccount(platform?.value)}
                      className="text-xs"
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Platform Selection & Message Composer */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Send Social Message</h3>
          </div>
          <div className="p-4 space-y-4">
            <Select
              label="Platform"
              value={selectedPlatform}
              onChange={setSelectedPlatform}
              options={platforms?.map(p => ({ value: p?.value, label: p?.label }))}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message Content
              </label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e?.target?.value)}
                placeholder="Compose your message..."
                className="w-full h-32 px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <span>{messageContent?.length}/500 characters</span>
                <span className={getPlatformColor(selectedPlatform)}>
                  <Icon name={getPlatformIcon(selectedPlatform)} size={14} className="inline mr-1" />
                  {platforms?.find(p => p?.value === selectedPlatform)?.label}
                </span>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button
                variant="default"
                iconName="Send"
                iconPosition="left"
                disabled={!messageContent || selectedProspects?.length === 0 || loading}
                onClick={handleSendMessage}
                className="flex-1"
              >
                {loading ? 'Sending...' : `Send Message (${selectedProspects?.length})`}
              </Button>
              <Button variant="outline" iconName="Clock" iconSize={16}>
                Schedule
              </Button>
            </div>
          </div>
        </div>

        {/* Prospects List */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Social Prospects</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {prospects?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No prospects available on {selectedPlatform}
              </div>
            ) : (
              prospects?.map((prospect) => (
                <div
                  key={prospect?.id}
                  className="p-4 border-b border-border last:border-b-0 hover:bg-muted transition-colors duration-200"
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={selectedProspects?.includes(prospect?.id)}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setSelectedProspects([...selectedProspects, prospect?.id]);
                        } else {
                          setSelectedProspects(selectedProspects?.filter(id => id !== prospect?.id));
                        }
                      }}
                    />
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                        src={prospect?.avatar}
                        alt={prospect?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-foreground truncate">
                          {prospect?.name}
                        </h4>
                        <Icon 
                          name={getPlatformIcon(selectedPlatform)} 
                          size={16} 
                          className={getPlatformColor(selectedPlatform)}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mb-2 truncate">
                        {prospect?.company}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded-full ${getConnectionStatusColor(prospect?.connectionStatus)}`}>
                          {prospect?.connectionStatus?.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Last: {prospect?.lastInteraction}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message History */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Recent Social Messages</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {socialMessages?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No social messages yet
              </div>
            ) : (
              socialMessages?.map((message) => (
                <div
                  key={message?.id}
                  className={`p-4 border-b border-border last:border-b-0 ${
                    message?.direction === 'inbound' ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon
                      name={getPlatformIcon(message?.metadata?.platform || selectedPlatform)}
                      size={16}
                      className={getPlatformColor(message?.metadata?.platform || selectedPlatform)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {message?.contact?.name || 'Unknown Contact'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {message?.created_at ? new Date(message.created_at)?.toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                      <p className="text-sm text-foreground mb-2 line-clamp-2">
                        {message?.content}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          message?.direction === 'inbound' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                        }`}>
                          {message?.direction}
                        </span>
                        <span className="text-xs text-success">
                          {message?.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {message?.metadata?.platform}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Campaign Analytics */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Social Campaigns</h3>
              <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
                New Campaign
              </Button>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {campaigns?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No social campaigns yet
              </div>
            ) : (
              campaigns?.map((campaign) => (
                <div
                  key={campaign?.id}
                  className="p-4 border border-border rounded-lg hover:bg-muted transition-colors duration-200"
                >
                  <div className="flex items-start space-x-3">
                    <Icon
                      name="Share2"
                      size={20}
                      className="text-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground mb-2">{campaign?.name}</h4>
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-foreground">{campaign?.sent_count || 0}</div>
                          <div className="text-xs text-muted-foreground">Sent</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-success">{campaign?.delivered_count || 0}</div>
                          <div className="text-xs text-muted-foreground">Delivered</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-primary">{campaign?.failed_count || 0}</div>
                          <div className="text-xs text-muted-foreground">Engaged</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-border">
                        <span className="text-xs text-muted-foreground">
                          Created: {campaign?.created_at ? new Date(campaign.created_at)?.toLocaleDateString() : 'Unknown'}
                        </span>
                        <Button variant="outline" size="sm" iconName="BarChart3" iconSize={14}>
                          Analytics
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaPanel;